import { TEMPLATES } from '@/features/campaigns/types';

export const TEMPLATE_OPTIONS = [
  { value: '0', label: TEMPLATES[0] ?? 'Simple IVR' },
  { value: '1', label: TEMPLATES[1] ?? 'DTMF' },
  { value: '2', label: TEMPLATES[2] ?? 'Call Patch' },
  { value: '3', label: TEMPLATES[3] ?? 'Custom IVR' },
  { value: '7', label: TEMPLATES[7] ?? 'TTS Simple IVR' },
  { value: '8', label: TEMPLATES[8] ?? 'TTS DTMF' },
  { value: '9', label: TEMPLATES[9] ?? 'TTS Call Patch' },
  { value: '10', label: 'Continuous Campaign' },
] as const;

export type TemplateId = (typeof TEMPLATE_OPTIONS)[number]['value'];

export const TTS_TEMPLATES = new Set<TemplateId>(['7', '8', '9']);
export const NEEDS_PROMPTS = new Set<TemplateId>(['0', '1', '2', '3']);
export const HAS_DTMF = new Set<TemplateId>(['1', '8']);
export const HAS_CALL_PATCH = new Set<TemplateId>(['2', '9']);
export const HAS_CUSTOM_IVR = new Set<TemplateId>(['3']);
export const IS_CONTINUOUS = (t: TemplateId) => t === '10';

/**
 * Per-template feature switches. Mirrors the legacy conditional UI:
 *   - voice files: which files (welcome/noinput/wronginput/thanks/noAgent)
 *     can be attached, and whether they're required vs optional.
 *   - menu: whether a menu prompt + wait/re-prompt fields show up.
 *   - dtmf picker: whether the multi-select DTMF input is rendered.
 *   - tts: whether the TTS rows + language/gender block applies.
 *   - SMS sections: which call-outcome SMS toggles render.
 */
export interface TemplateFeatures {
  needsMenu: boolean;
  needsMenuWait: boolean; // shows the menuWaitTime + rePrompt inputs
  hasDtmfPicker: boolean;
  hasPatchList: boolean;
  hasIvr: boolean;
  hasTts: boolean;
  hasTtsFlow: boolean;
  /** Required voice files (no toggle). */
  requiredVoiceFiles: Array<
    'welcome' | 'menu' | 'noinput' | 'wronginput' | 'thanks' | 'noAgent'
  >;
  /** Optional voice files exposed via a toggle. */
  optionalVoiceFiles: Array<
    'welcome' | 'noinput' | 'wronginput' | 'thanks'
  >;
  /** SMS sections shown in Step 5. */
  smsSections: Array<'success' | 'fail' | 'dtmf' | 'patchSuccess' | 'patchFail'>;
}

const NONE: TemplateFeatures = {
  needsMenu: false,
  needsMenuWait: false,
  hasDtmfPicker: false,
  hasPatchList: false,
  hasIvr: false,
  hasTts: false,
  hasTtsFlow: false,
  requiredVoiceFiles: [],
  optionalVoiceFiles: [],
  smsSections: [],
};

export const TEMPLATE_FEATURES: Record<TemplateId, TemplateFeatures> = {
  '0': {
    ...NONE,
    requiredVoiceFiles: ['welcome'],
    smsSections: ['success', 'fail'],
  },
  '1': {
    ...NONE,
    needsMenu: true,
    needsMenuWait: true,
    hasDtmfPicker: true,
    requiredVoiceFiles: ['menu'],
    optionalVoiceFiles: ['welcome', 'noinput', 'wronginput', 'thanks'],
    smsSections: ['success', 'fail', 'dtmf'],
  },
  '2': {
    ...NONE,
    needsMenu: true,
    needsMenuWait: true,
    hasPatchList: true,
    requiredVoiceFiles: ['menu', 'noAgent'],
    optionalVoiceFiles: ['welcome', 'noinput', 'wronginput', 'thanks'],
    smsSections: ['success', 'fail', 'dtmf', 'patchSuccess', 'patchFail'],
  },
  '3': {
    ...NONE,
    hasIvr: true,
  },
  '7': {
    ...NONE,
    hasTts: true,
    smsSections: ['success', 'fail'],
  },
  '8': {
    ...NONE,
    needsMenu: true,
    needsMenuWait: true,
    hasDtmfPicker: true,
    hasTts: true,
    requiredVoiceFiles: ['menu'],
    optionalVoiceFiles: ['noinput', 'wronginput', 'thanks'],
    smsSections: ['success', 'fail', 'dtmf'],
  },
  '9': {
    ...NONE,
    needsMenu: true,
    needsMenuWait: true,
    hasPatchList: true,
    hasTts: true,
    requiredVoiceFiles: ['menu', 'noAgent'],
    optionalVoiceFiles: ['noinput', 'wronginput', 'thanks'],
    smsSections: ['success', 'fail', 'patchSuccess', 'patchFail'],
  },
  '10': {
    ...NONE,
    hasTtsFlow: true,
  },
};

export function templateFeatures(t: TemplateId): TemplateFeatures {
  return TEMPLATE_FEATURES[t] ?? NONE;
}

/** Menu wait time + re-prompt validation ranges. */
export const MENU_WAIT_MIN = 1;
export const MENU_WAIT_MAX = 10;
export const RE_PROMPT_MIN = 0;
export const RE_PROMPT_MAX = 3;
/** Retry interval (minutes) — clamped on blur. */
export const RETRY_INTERVAL_MIN = 1;
export const RETRY_INTERVAL_MAX = 200;

/* ---------------- Lookups ---------------- */

export interface BaseUploadRow {
  baseId: number;
  baseName: string;
  uploadedDate?: string;
  totalNumbers?: number;
}

export interface PromptUploadRow {
  promptId: number;
  promptName?: string;
  /** Friendly file name — legacy renders this rather than promptName. */
  fileName?: string;
  /** "welcome" | "menu" | "noinput" | "wronginput" | "thanks" | "noAgent". */
  promptCategory?: string;
  /** 1 = ready, anything else = pending/disabled. */
  promptStatus?: number | string;
}

export interface TtsTypeRow {
  /** "text" | "number" | "date" | "time" | "currency", from /api/obd/tts/type/list. */
  tts_type: string;
}

export interface UserLocationRow {
  locationId: number;
  locationName: string;
}

export interface UserCliRow {
  cliId: number;
  cli: string;
  pilotNumber?: string | number;
}

export interface DedicatedCliRow {
  cliId: number;
  cli: string;
  allocated?: number;
}

/**
 * Wire shape returned by `GET /api/obd/custom/ivr/list/{userId}`. The
 * backend ships `id` + `flowName` (legacy naming) — not `ivrId` / `ivrName`
 * as I'd assumed. Keep these field names exactly to match the response.
 */
export interface IvrFlowRow {
  id: number;
  flowName: string;
}

/**
 * Wire shape returned by `GET /api/obd/tts/flow/list/{userId}`. Backend
 * ships `id` + `ttsFlowName` (legacy table column names). Not
 * `ttsFlowId` / `flowName` as the earlier (speculative) type expected.
 */
export interface TtsFlowRow {
  id: number;
  ttsFlowName: string;
}

export interface UserLanguageRow {
  language: string;
  gender?: string;
}

export interface UserGroupAgentRow {
  groupId: number;
  groupName: string;
}

export interface UserModuleRow {
  moduleName: string;
  moduleId?: number;
}

/* ---------------- Compose form values ---------------- */

export interface PatchListRow {
  /** The agent group bound to this DTMF digit. */
  groupId: string;
  groupName: string;
  /** Single DTMF digit the user presses to reach this group. */
  agentDtmf: string;
}

/**
 * Single entry in the legacy ttsList. `ttsType: 'prompt'` rows bind a
 * pre-recorded prompt id; `ttsType: 'variable'` rows bind a numbered
 * variable (1..N) plus a typed format (`text` / `number` / `date` /
 * `time` / `currency`).
 */
export interface TtsRow {
  ttsType: 'prompt' | 'variable';
  /** Prompt id (for `prompt`) or 1-based variable index (for `variable`). */
  value: string;
  /** Empty for `prompt`; one of the TTS types for `variable`. */
  variableType: string;
}

export type BaseFileMode = 'contactList' | 'newBaseFile' | 'oldBaseFile';

export interface VoiceCampaignFormValues {
  /* Step 1 - Basics */
  campaignName: string;
  templateId: TemplateId;
  sendLater: boolean;
  scheduleDate: string; // ISO; empty string means "send now"
  retries: number;
  retryInterval: number; // minutes; server multiplies by 60

  /* Step 2 - Contacts */
  baseFileMode: BaseFileMode;
  contactList: string;
  baseName: string;
  oldBaseId: string;

  /* Step 3 - Content */
  /** Optional voice file toggles (match the legacy welcomePrompt etc. flags). */
  useWelcomeFile: boolean;
  useNoinputFile: boolean;
  useWronginputFile: boolean;
  useThanksFile: boolean;
  welcomeId: string;
  menuId: string;
  noinputId: string;
  wronginputId: string;
  thanksId: string;
  rePrompt: string;
  repeatMenu: string;
  menuWaitTime: string;
  dtmf: string[]; // selected DTMF options for templates 1/8
  patchList: PatchListRow[];
  noAgentId: string;
  ivrId: string;
  language: string;
  gender: string;
  ttsRows: TtsRow[];
  selectedTTSFlowId: string;

  /* Step 4 - Routing */
  locationIds: string[];
  selectedCLI: string[];

  /* Step 5 - Notifications */
  sendSmsSuccess: boolean;
  smsApiSuccess: string;
  callDurationSMS: number;
  sendSmsFail: boolean;
  smsApiFail: string;
  sendSmsDtmf: boolean;
  smsApiDtmf: string;
  sendSmsPatchSuccess: boolean;
  callPatchSuccessMessage: string;
  sendSmsPatchFail: boolean;
  callPatchFailMessage: string;
  sendWebhook: boolean;
  webhookId: string;
}

export const DEFAULT_FORM_VALUES: VoiceCampaignFormValues = {
  campaignName: '',
  templateId: '0',
  sendLater: false,
  scheduleDate: '',
  retries: 0,
  retryInterval: 0,
  baseFileMode: 'contactList',
  contactList: '',
  baseName: '',
  oldBaseId: '',
  useWelcomeFile: false,
  useNoinputFile: false,
  useWronginputFile: false,
  useThanksFile: false,
  welcomeId: '',
  menuId: '',
  noinputId: '',
  wronginputId: '',
  thanksId: '',
  rePrompt: '',
  repeatMenu: '',
  menuWaitTime: '',
  dtmf: [],
  patchList: [],
  noAgentId: '',
  ivrId: '',
  language: '',
  gender: '',
  ttsRows: [],
  selectedTTSFlowId: '',
  locationIds: [],
  selectedCLI: [],
  sendSmsSuccess: false,
  smsApiSuccess: '',
  callDurationSMS: 0,
  sendSmsFail: false,
  smsApiFail: '',
  sendSmsDtmf: false,
  smsApiDtmf: '',
  sendSmsPatchSuccess: false,
  callPatchSuccessMessage: '',
  sendSmsPatchFail: false,
  callPatchFailMessage: '',
  sendWebhook: false,
  webhookId: '',
};

/* ---------------- Wire format ---------------- */

export interface ComposeCampaignRequest {
  userId: number;
  campaignName: string;
  templateId: string;
  scheduleTime: string;
  baseId: number;
  dtmf: string;
  welcomePId: string;
  menuPId: string;
  noInputPId: string;
  wrongInputPId: string;
  thanksPId: string;
  smsSuccessApi: string;
  smsFailApi: string;
  smsDtmfApi: string;
  callDurationSMS: number;
  retries: number;
  retryInterval: number; // seconds (already multiplied)
  agentRows: string;
  menuWaitTime: string;
  rePrompt: string;
  ivrId: string;
  location: string;
  clis: string;
  webhook: boolean;
  webhookId: string;
  ttsRows: string;
  gender: string;
  language: string;
  noAgentId: string;
  callPatchSuccessMessage: string;
  callPatchFailMessage: string;
  ttsFlowId: string;
  repeatMenu: string;
}

export type UpdateCampaignRequest = ComposeCampaignRequest & {
  campaignId: number;
};
